import * as React from 'react';
import { useState } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import ProtectedRoute from './auth/ProtectedRoute';
import { LocationProvider } from '../context/LocationContext';
import { usePageTracking } from '../hooks/useAnalytics';
import { calcRouteChunks } from './charts/ChartsSlice';
import { cacheChartSettings } from './chartSettings/ChartSettingsSlice';

// UI components (layout-related)
import TopNavBar from './home/components/TopNavBar';
import MobileBottomNavBar from './home/components/MobileBottomNavBar';
import BackToTopButton from './home/components/BackToTopButton';
import FeedbackFab from './feedback/FeedbackFab';
import MobileChartSettingsDrawer from './cues/MobileChartSettingsDrawer';

// Route configuration
import { routeConfig } from '../config/routes';

function AppNavRouter() {
  return (
    <Router basename={process.env.PUBLIC_URL}>
      <AppLayout />
    </Router>
  );
}

function AppLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [MobileChartSettingsDrawerOpen, setMobileChartSettingsDrawerOpen] = useState(false);
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const routeData = useSelector(state => state.charts.route_json);
  const chartSettings = useSelector(state => state.chartSettings.chartSettings);
  const chartData = useSelector(state => state.charts.chartData);

  // Track page views on route changes
  usePageTracking();

  return (
    <>
      <TopNavBar />
      <Box className="print-overflow-auto print-all-charts" sx={{
        width: '100%',
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        maxWidth: '100vw',
        paddingTop: '64px'
      }}>
        <Routes>
          <Route path="/" element={<routeConfig.main.Home />} />
          <Route path="/routes" element={<ProtectedRoute />} >
            <Route index element={<routeConfig.main.RouteSelection />} />
          </Route>
          <Route path="/pricing" element={<routeConfig.static.Pricing />} />
          <Route path="/rwgps-success" element={<routeConfig.auth.RWGPSSuccess />} />
          <Route path="/strava-success" element={<routeConfig.auth.StravaSuccess />} />
          <Route path="/settings" element={<ProtectedRoute />} >
            <Route index element={<routeConfig.main.AppSettings />} />
          </Route>
          <Route path="/cues/:source/:routeId" element={<ProtectedRoute />} >
            <Route index element={<LocationProvider><routeConfig.main.Cues /></LocationProvider>} />
          </Route>
          <Route path="/cues/:routeId" element={<ProtectedRoute />} >
            <Route index element={<LocationProvider><routeConfig.main.Cues /></LocationProvider>} />
          </Route>
          <Route path="/help" element={<routeConfig.static.Help />} />
          <Route path="/updates" element={<routeConfig.static.ChangeLog />} />
          <Route path="/integrations" element={<routeConfig.static.Integrations />} />
          <Route path="/about" element={<routeConfig.static.About />} />
          <Route path="/terms" element={<routeConfig.static.Terms />} />
          <Route path="/privacy" element={<routeConfig.static.Privacy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <routeConfig.utility.UpdateNotification />
        <routeConfig.utility.FeedbackSurvey />
      </Box>

      <BackToTopButton />

      {/* Mobile Footer Navigation - Only show when user is authenticated */}
      {isMobile && user && (
        <MobileBottomNavBar onRouteConfigOpen={() => setMobileChartSettingsDrawerOpen(true)} />
      )}

      <FeedbackFab />

      {/* Placed here to overlay everything else????? */}
      <MobileChartSettingsDrawer
        isMobile={isMobile}
        drawerOpen={MobileChartSettingsDrawerOpen}
        onClose={() => setMobileChartSettingsDrawerOpen(false)}
        onSave={(e) => {
          e?.stopPropagation();
          if (isMobile) {
            setMobileChartSettingsDrawerOpen(false);
          }
          dispatch(calcRouteChunks({ chartSettings, trackPointsWithPoi: chartData }));
          if (routeData?.isCached) {
            dispatch(cacheChartSettings({ routeId: routeData.id, chartSettings }));
          }
        }}
        routeData={routeData}
      />
    </>
  );
}


export default AppNavRouter;